import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Medal, Award, Star, Sparkles, Calendar } from 'lucide-react';

const API = `${window.location.origin}/api`;

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

export const periodLabel = (period) => {
  if (!period) return '';
  const [y, m] = period.split('-');
  return `${TR_MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

const RANK_STYLE = {
  1: { Icon: Trophy, bg: 'bg-amber-50', ring: 'ring-amber-200', text: 'text-amber-600', label: '1.' },
  2: { Icon: Medal, bg: 'bg-slate-100', ring: 'ring-slate-200', text: 'text-slate-500', label: '2.' },
  3: { Icon: Award, bg: 'bg-orange-50', ring: 'ring-orange-200', text: 'text-orange-700', label: '3.' },
};

const RankCard = ({ badge }) => {
  const s = RANK_STYLE[badge.rank] || RANK_STYLE[1];
  const Icon = s.Icon;
  const isFirst = badge.rank === 1;

  if (isFirst) {
    return (
      <div
        data-testid="badge-card"
        className="flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-br from-amber-200 to-yellow-100 border border-amber-300 ring-1 ring-amber-300 shadow-md transition-transform duration-200 hover:-translate-y-0.5"
      >
        <div className="w-12 h-12 rounded-xl bg-amber-400/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-7 h-7 text-amber-700" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-bold text-amber-800 leading-tight">1. Sıra</div>
          <div className="text-base font-bold text-gray-900 truncate">{badge.userName}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="badge-card"
      className={`flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-gray-100 ring-1 ${s.ring} shadow-sm transition-transform duration-200 hover:-translate-y-0.5`}
    >
      <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 ${s.text}`} />
      </div>
      <div className="min-w-0">
        <div className={`text-[11px] font-bold ${s.text} leading-tight`}>{s.label} Sıra</div>
        <div className="text-sm font-semibold text-gray-900 truncate">{badge.userName}</div>
      </div>
    </div>
  );
};

const MentionCard = ({ badge }) => (
  <div
    data-testid="mention-card"
    className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-fuchsia-100 ring-1 ring-fuchsia-200 shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
  >
    <div className="w-9 h-9 rounded-lg bg-fuchsia-50 flex items-center justify-center flex-shrink-0">
      <Star className="w-5 h-5 text-fuchsia-500" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-gray-900 truncate">{badge.userName}</div>
      {badge.reason && <div className="text-[11px] text-gray-500 truncate italic">"{badge.reason}"</div>}
    </div>
  </div>
);

export default function BadgeAnnouncement() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    const url = period ? `${API}/badges/dashboard?period=${period}` : `${API}/badges/dashboard`;
    axios.get(url)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [period]);

  if (!data || !data.dashboardEnabled) return null;
  const perf = data.performance || [];
  const mention = data.mention || [];
  const availablePeriods = data.availablePeriods || [];

  // Hiç rozet kaydı yoksa bilgilendirme mesajı göster
  if (perf.length === 0 && mention.length === 0) {
    return (
      <div data-testid="badge-announcement-empty" className="glass rounded-2xl p-5 sm:p-6 mb-6">
        <div className="flex flex-col items-center justify-center text-center py-6">
          <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mb-3">
            <Trophy className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Henüz rozet kaydı yok</h2>
          <p className="text-sm text-gray-500 mt-1">Rozetler hesaplandığında ayın performans birincileri burada görünecek.</p>
        </div>
      </div>
    );
  }

  // Kategori bazında grupla
  const groupsMap = {};
  perf.forEach((b) => {
    if (!groupsMap[b.tagId]) groupsMap[b.tagId] = { tagName: b.tagName, tagColor: b.tagColor || '#6366f1', items: [] };
    groupsMap[b.tagId].items.push(b);
  });
  const groups = Object.values(groupsMap)
    .map((g) => ({ ...g, items: g.items.sort((a, b) => (a.rank || 99) - (b.rank || 99)) }))
    .sort((a, b) => (a.tagName || '').localeCompare(b.tagName || '', 'tr'));

  return (
    <div data-testid="badge-announcement" className="glass rounded-2xl p-5 sm:p-6 mb-6">
      {/* Başlık */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Trophy className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Ayın Performans Rozetleri</h2>
            <p className="text-xs text-gray-500"><span className="font-bold text-gray-700">{periodLabel(data.period)} dönemi</span> · Emeği geçen herkesi tebrik ederiz!</p>
          </div>
        </div>

        {/* Dönem seçimi */}
        {availablePeriods.length > 0 && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm flex-shrink-0">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              id="badge-period-filter"
              data-testid="badge-period-filter"
              value={data.period}
              onChange={(e) => setPeriod(e.target.value)}
              className="bg-transparent text-sm font-medium text-gray-900 focus:outline-none cursor-pointer pr-1"
            >
              {availablePeriods.map((p) => (
                <option key={p} value={p}>{periodLabel(p)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Kategori grupları */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {groups.map((g) => (
          <div key={g.tagName} data-testid="badge-category-group" className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: g.tagColor }} />
              <span className="text-sm font-bold text-gray-800">{g.tagName}</span>
            </div>
            <div className="space-y-2">
              {g.items.map((b) => <RankCard key={b.id} badge={b} />)}
            </div>
          </div>
        ))}

        {/* Mansiyon ayrı grup */}
        {mention.length > 0 && (
          <div data-testid="mention-group" className="rounded-xl border border-fuchsia-100 bg-fuchsia-50/50 p-3">
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles className="w-4 h-4 text-fuchsia-500 flex-shrink-0" />
              <span className="text-sm font-bold text-fuchsia-700">Özel Mansiyon</span>
            </div>
            <div className="space-y-2">
              {mention.map((b) => <MentionCard key={b.id} badge={b} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
